import {observer} from 'mobx-react-lite';
import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EAutocomplete,
  EForm,
  ELabel,
  ESmallButton,
} from 'webapp-lib/Components';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';

import {ActionButtons, Loading} from '#';
import handledBackend, {
  ObligationResponse,
  ResolvableObligationVersionDeadlineReachedNotification,
} from '@lexian/api/notifications-api';
import {Obligation} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {State, unexpectedError} from '_/utilities';
import {EFDateInput, EFTextArea, useForm} from '_/utilities/form';
import {Loadable} from '_/utilities/types';

import {StoreContext} from '../../store';
import {NewObligationState} from '../../store/store';

import {ExclamationMark, FormHeader, HeaderRow, TextWrapper} from './styles';

enum ResolvableObligationVersionDeadlineReachedFormFields {
  Note = 'note',
  Deadline = 'deadline',
}

const isObligationResponse = (
  obligation: unknown
): obligation is ObligationResponse =>
  Boolean((obligation as ObligationResponse).id);

const UResolvableObligationVersionDeadlineReached = ({
  notification,
}: {
  notification: ResolvableObligationVersionDeadlineReachedNotification;
}): React.ReactElement => {
  const [loadable, setLoadable] = useState<Loadable<ObligationResponse>>({
    state: State.Loading,
  });
  const {t} = useTranslation<I18NextNS>('notifications');
  const store = useContext(StoreContext);

  useEffect(() => {
    handledBackend.getObligation({
      parameters: {
        obligationId: notification.entity.id,
      },
      onSuccess: ({body}) => {
        setLoadable(
          isObligationResponse(body)
            ? {
                state: State.Success,
                data: body,
              }
            : {state: State.Error}
        );
      },
      localHandlers: {
        '404': async () => {
          if (store.activeNotification) {
            await store.deleteNotification({
              notificationId: store.activeNotification.id,
            });
          }
          showSnackbar({
            text: t('snackbar.error.obligationNotAccessible'),
            type: SnackbarType.WARNING,
          });
        },
        defaultErrorCondition: () => {
          unexpectedError();
        },
      },
    });
  }, [setLoadable, notification.entity, store, t]);

  return (
    <div>
      <HeaderRow>
        <Obligation />
        <ExclamationMark show={notification.importantFlag} />
        <TextWrapper>{notification.message}</TextWrapper>
      </HeaderRow>
      {loadable.state === State.Success ? (
        <ResolvableObligationVersionDeadlineReachedForm
          notification={notification}
          obligation={loadable.data!}
        />
      ) : (
        <Loading />
      )}
    </div>
  );
};

const ResolvableObligationVersionDeadlineReachedForm = ({
  notification,
  obligation,
}: {
  notification: ResolvableObligationVersionDeadlineReachedNotification;
  obligation: ObligationResponse;
}) => {
  const {t} = useTranslation<I18NextNS>('notifications');
  const store = useContext(StoreContext);

  const setNotificationState = useCallback(
    (state: NewObligationState) => {
      store.resolveObligation({
        notificationId: notification.id,
        newState: state,
      });
    },
    [notification.id, store]
  );

  const {handleSubmit, control} = useForm({
    defaultValues: {
      [ResolvableObligationVersionDeadlineReachedFormFields.Note]:
        obligation.note,
      [ResolvableObligationVersionDeadlineReachedFormFields.Deadline]:
        obligation.deadline,
    },
    onSubmit: () => {},
  });
  return (
    <EForm onSubmit={handleSubmit}>
      <FormHeader>
        {t('obligationVersionDeadlineReached.obligationDetail')}
      </FormHeader>
      <EFTextArea
        control={control}
        label={t('obligationVersionDeadlineReached.note')}
        name={ResolvableObligationVersionDeadlineReachedFormFields.Note}
        disabled
      />
      <EFDateInput
        control={control}
        label={t('obligationVersionDeadlineReached.deadline')}
        name={ResolvableObligationVersionDeadlineReachedFormFields.Deadline}
        disabled
      />
      <EAutocomplete
        label={t('obligationVersionDeadlineReached.assignee')}
        options={[
          {
            value: obligation.responsibleUser.id,
            displayText: obligation.responsibleUser.displayName,
          },
        ]}
        value={obligation.responsibleUser.id}
        disabled
      />
      <ELabel>{t('obligations:obligationsList.actions.setStatus')}</ELabel>
      <ActionButtons>
        <ESmallButton
          onClick={() => {
            setNotificationState('FULFILLED');
          }}
        >
          {t('obligations:states.fulfilled')}
        </ESmallButton>
        <ESmallButton
          onClick={() => {
            setNotificationState('MISSED');
          }}
        >
          {t('obligations:states.missed')}
        </ESmallButton>
        <ESmallButton
          onClick={() => {
            setNotificationState('OBSOLETE');
          }}
        >
          {t('obligations:states.obsolete')}
        </ESmallButton>
      </ActionButtons>
    </EForm>
  );
};

export const ResolvableObligationVersionDeadlineReached = observer(
  UResolvableObligationVersionDeadlineReached
);
